import { debounce } from 'lodash';
import { makeAutoObservable, observable, reaction } from 'mobx';

import { LogManager } from '../utility/Logger';
import { ActionType, ActionEventType } from '@src/monitoring/types';

import type { Renderer } from '../renderer/Renderer';
import type { RendererState } from '../renderer/RendererState';
import type { Logger } from '../utility/Logger';
import type { RootStore } from './RootStore';
import type { VideoSource } from './types';

// Type for state change callbacks
export type StateChangeCallback = (
  state: RendererState,
) => void | Promise<void>;

export type SourceRefreshCallback = (source: VideoSource) => void;

// TODO: Make video source a type?

export class VideoSourceStore {
  private _source: VideoSource = {}; // Live updated source object
  // private _state: RendererState | null = null; // Most recent renderer state
  private _renderer: Renderer | null = null;
  private _logger: Logger;
  private _debouncedSetSource: ReturnType<
    typeof debounce<
      (params: { source: VideoSource; createUndoPoint: boolean }) => void
    >
  > | null = null;
  // After migrating to “always set full source”, incremental modifications
  // are folded immediately into the local `_source` and the renderer is
  // updated through the same debounced `setSource` path.  These fields are
  // kept only to satisfy references that will be removed later in this
  // patch.
  // TODO: remove in a follow-up refactor once all code paths are updated.
  private _debounceTimeMs = 300; // Default debounce time in milliseconds
  private _originalOnStateChange:
    | ((state: RendererState) => void | Promise<void>)
    | null = null;
  private _stateChangeCallbacks = new Set<StateChangeCallback>();
  private _oneTimeStateChangeCallbacks: Array<{
    callback: StateChangeCallback;
    fired: boolean;
  }> = []; // Callbacks that run only once on state change

  private _oneTimeSourceRefreshCallbacks: Array<{
    callback: SourceRefreshCallback;
    fired: boolean;
  }> = []; // Callbacks that run only once on source refresh

  // Removing write lock mechanism as it causes issues with unrelated updates
  private _debounceSourceRefreshMs = 300; // Time to wait after state change before refreshing source
  private _lastStateChangeTime = 0; // Track when the last state change occurred
  private _pendingSourceRefreshTimeoutId: ReturnType<typeof setTimeout> | null =
    null;
  private _hasPendingSetSource = false;
  private _sourceMediaDurationMap = new Map<string, number | null>();

  // Workflow tracking for video loading monitoring
  currentVideoLoadingWorkflowId: string | null = null;

  constructor(private rootStore: RootStore) {
    this._logger = LogManager.getLogger('video-source-store');

    // Log a test message to verify format
    this._logger.info('VideoSourceStore initialized');

    // Make the store fully observable.  We keep `_source` observable *by
    // reference* so MobX doesn't walk the huge nested object – we only care
    // about replacements, not deep mutations.
    makeAutoObservable(
      this,
      {
        _source: observable.ref,
        _scheduleSourceRefresh: false,
        _renderer: false,
        _debouncedSetSource: false,
        _cleanupTimeouts: false,
        _stateChangeCallbacks: false,
        _oneTimeStateChangeCallbacks: false,
        _oneTimeSourceRefreshCallbacks: false,
      } as any,
      { autoBind: true },
    );
  }

  /**
   * Get the current source object
   */
  get source(): Readonly<Record<string, any>> {
    return this._source;
  }

  get hasPendingSetSource(): boolean {
    return this._hasPendingSetSource;
  }

  get hasDebouncedSourceRefresh(): boolean {
    return (
      this._pendingSourceRefreshTimeoutId !== null || this._hasPendingSetSource // pending set source will also trigger a refresh
    );
  }

  resetSource(): void {
    this._source = {};
  }

  /**
   * Get the current renderer state
   */
  // get state(): RendererState | null {
  //   return this._state;
  // }

  setDuration(d: number | null): void {
    this._logger.info('Setting duration in store', { d });

    // Always update our local copy of the source
    this._source.duration = d;

    // If we have a renderer and debounced function
    if (this._renderer && this._debouncedSetSource) {
      this._hasPendingSetSource = true;
      // Call the debounced function which will eventually send to the renderer
      this._debouncedSetSource({
        source: this._source,
        createUndoPoint: false,
      });
    }
  }

  /**
   * Modify elements in the source by function
   * @param modifyFn Function to modify elements
   * @param createUndoPoint Whether to create an undo point (defaults to false)
   * @returns The current source object
   */
  modifyElements(
    modifyFn: (elements: Record<string, any>[]) => Record<string, any>[],
    createUndoPoint = false,
  ): Record<string, any> {
    this._logger.info('Modifying elements in store', {
      sourceElementCount: this._source.elements?.length,
    });
    // Always update our local copy of the source
    this._source.elements = modifyFn(this._source.elements || []);
    // If we have a renderer and debounced function
    if (this._renderer && this._debouncedSetSource) {
      this._hasPendingSetSource = true;
      // Call the debounced function which will eventually send to the renderer
      this._debouncedSetSource({
        source: this._source,
        createUndoPoint,
      });
    }

    return this.source;
  }

  async flushAndGetSource(): Promise<Record<string, any>> {
    // Flush any pending debounced changes and wait for completion
    await this.flushChanges();
    // Return the current source
    return this._source;
  }

  /**
   * Set the source with debounce
   * @param source Source object to set
   * @param createUndoPoint Whether to create an undo point (defaults to false)
   */
  setSource(source: Record<string, any>, createUndoPoint = false): void {
    // If we don't have a renderer, just update the local source
    if (!this._renderer || !this._renderer.ready) {
      this._logger.info('Setting source without renderer', {
        sourceElementCount: source.elements?.length,
      });
      this._source = source;
      return;
    }

    this._logger.info('Setting source in store', {
      sourceElementCount: source.elements?.length,
    });

    // console.trace('VideoSourceStore: setSource', toJS(source));

    // Always update our local copy of the source
    this._source = source;

    // If we have a renderer and debounced function
    if (this._renderer && this._debouncedSetSource) {
      this._hasPendingSetSource = true;
      // Call the debounced function which will eventually send to the renderer
      this._debouncedSetSource({ source, createUndoPoint });
    }
  }

  /**
   * Apply modifications with debounce
   * This method immediately updates the local source and collects modifications
   * for delayed application to the renderer
   * @param modifications Modifications to apply
   */
  applyModifications(modifications: Record<string, any>): void {
    // Apply modifications to a *new* object to keep `_source` immutable
    const newSource: Record<string, any> = JSON.parse(
      JSON.stringify(this._source),
    );

    this._logger.info(
      'Applying modifications to source',
      modifications,
      newSource,
    );

    this._applyModificationsToObject(newSource, modifications);

    this.setSource(newSource, false);
  }

  /**
   * Apply modifications directly to the internal source object
   * @param modifications Modifications to apply to the source
   */
  /**
   * Apply dotted path modifications into a target object (mutates target).
   */
  private _applyModificationsToObject(
    target: Record<string, any>,
    modifications: Record<string, any>,
  ): void {
    // Apply each modification to the local source
    for (const [path, value] of Object.entries(modifications)) {
      // Parse path to find the correct place in the source object
      const pathParts = path.split('.');

      // Get the first part of the path - could be element ID or top-level property
      const firstPart = pathParts[0];

      // If modifying a top-level source property, update it directly
      if (pathParts.length === 1) {
        (target as any)[firstPart] = value;
        continue;
      }

      // Ensure elements array exists
      if (!target.elements) {
        target.elements = [];
      }

      // Check if this is an element property update (e.g., "element-id.property")
      const targetElement = this._findElementById(target.elements, firstPart);

      if (targetElement) {
        // This is an element property update
        this._applyPropertyPathToObject(
          targetElement,
          pathParts.slice(1),
          value,
        );
      } else {
        // This is a nested property of the root source object
        this._applyPropertyPathToObject(target, pathParts, value);
      }
    }
  }

  private _findElementById(
    elements: Record<string, any>[],
    id: string,
  ): Record<string, any> | undefined {
    for (const element of elements) {
      if (element.id === id) {
        return element;
      }

      if ('elements' in element) {
        const foundNestedElement = this._findElementById(element.elements, id);
        if (foundNestedElement) {
          return foundNestedElement;
        }
      }
    }
    return undefined;
  }

  /**
   * Apply a property path to an object
   * Handles arbitrary nesting levels (e.g., "prop1.prop2.prop3")
   * and array indices (e.g., "animation.opacity.1.value")
   * @param obj The object to update
   * @param pathParts The property path parts
   * @param value The value to set
   */
  private _applyPropertyPathToObject(
    obj: Record<string, any>,
    pathParts: string[],
    value: any,
  ): void {
    const [first, ...rest] = pathParts;

    if (rest.length === 0) {
      // Base case: set the value directly
      obj[first] = value;
    } else {
      // Handle numeric indices for arrays
      const isArrayIndex = !isNaN(Number(first)) && Array.isArray(obj);
      const index = isArrayIndex ? Number(first) : first;

      // Create nested object/array if it doesn't exist or has the wrong type
      if (!obj[index]) {
        // If the next path part is a number, create an array
        const nextIsNumber = !isNaN(Number(rest[0]));
        obj[index] = nextIsNumber ? [] : {};
      } else if (typeof obj[index] !== 'object') {
        // Convert to object if it's not already an object (e.g., if it was a primitive)
        const nextIsNumber = !isNaN(Number(rest[0]));
        obj[index] = nextIsNumber ? [] : {};
      }

      // Recursively apply the rest of the path
      this._applyPropertyPathToObject(obj[index], rest, value);
    }
  }

  /**
   * Connect a renderer to this store
   * @param renderer Renderer instance
   */
  connectRenderer(renderer: Renderer): void {
    this._renderer = renderer;
    this._setupDebouncedFunctions();

    // We need to save the original handler before setting up our own
    this._originalOnStateChange = renderer.onStateChange || null;

    // Setup our state change handler
    this._setupStateChangeHandler();

    // Load the initial source from the renderer if we don't have one
    if (!this._source || Object.keys(this._source).length === 0) {
      this._source = renderer.getSource();
    }

    // // Setup reaction to monitor renderer state changes
    // reaction(
    //   () => this._renderer?.state,
    //   (state) => {
    //       // Update our local source when renderer state changes
    //       this._source = this._renderer!.getSource();
    //       this._state = state;
    //     }
    //   },
    // );
  }

  /**
   * Disconnect the current renderer
   */
  disconnectRenderer(): void {
    if (this._renderer && this._originalOnStateChange) {
      // Restore original onStateChange handler
      this._renderer.onStateChange = this._originalOnStateChange;
    }

    // Clean up any pending timeouts
    this._cleanupTimeouts();

    this._renderer = null;
    this._debouncedSetSource = null;
    this._originalOnStateChange = null;
    this._oneTimeStateChangeCallbacks = [];
    this._lastStateChangeTime = 0;
  }

  /**
   * Register a callback for state changes
   * @param callback The callback function to execute when state changes
   * @returns A function to unregister the callback
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this._stateChangeCallbacks.add(callback);
    return () => {
      this._stateChangeCallbacks.delete(callback);
    };
  }

  /**
   * Lightweight observable helper – lets consumers react whenever the entire
   * `_source` object reference changes (which is the pattern we use when
   * syncing from the headless editor).
   */
  onSourceChange(cb: (src: VideoSource) => void) {
    return reaction(
      () => this._source,
      src => {
        cb(src as VideoSource);
      },
    );
  }

  /**
   * Register a callback to be executed only once after the next source refresh
   * @param callback The callback function to execute once after the next source refresh
   * @returns A function to cancel the one-time callback before it fires
   */
  onNextSourceRefresh(
    callback: (source: VideoSource) => void | Promise<void>,
  ): () => void {
    // Register a one-time callback that will be executed after the next scheduled source refresh
    const callbackEntry = { callback, fired: false };
    this._oneTimeSourceRefreshCallbacks.push(callbackEntry);
    return () => {
      // Remove the callback if it hasn't fired yet
      const index = this._oneTimeSourceRefreshCallbacks.findIndex(
        entry => entry === callbackEntry,
      );
      if (index !== -1) {
        this._oneTimeSourceRefreshCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Register a callback to be executed only once after the next state change
   * @param callback The callback function to execute once after the next state change
   * @returns A function to cancel the one-time callback before it fires
   */
  onNextStateChange(callback: StateChangeCallback): () => void {
    const callbackEntry = { callback, fired: false };
    this._oneTimeStateChangeCallbacks.push(callbackEntry);

    return () => {
      const index = this._oneTimeStateChangeCallbacks.findIndex(
        entry => entry === callbackEntry,
      );
      if (index !== -1) {
        this._oneTimeStateChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Flush any pending debounced changes immediately
   * @returns A promise that resolves when the flush is complete (after state change)
   */
  async flushChanges(): Promise<void> {
    // If there's nothing to flush, just return
    if (!this.hasPendingSetSource) {
      return Promise.resolve();
    }

    // Clear any pending source refresh
    this._cleanupTimeouts();

    // Flush the debounced setSource
    this._logger.info('Flushing debounced setSource');
    const flushResult = this._debouncedSetSource?.flush();

    // Wait for the next state change to ensure flush is complete if
    // we in fact did flush something.
    if (flushResult) {
      return new Promise<void>(resolve => {
        this.onNextStateChange(() => {
          this._logger.info('Flush completed');
          resolve();
        });
      });
    }
  }

  /**
   * Clean up any pending timeouts
   * @private
   */
  private _cleanupTimeouts(): void {
    if (this._pendingSourceRefreshTimeoutId !== null) {
      clearTimeout(this._pendingSourceRefreshTimeoutId);
      this._pendingSourceRefreshTimeoutId = null;
    }
  }

  /**
   * Setup the debounced functions with current settings
   */
  private _setupDebouncedFunctions(): void {
    if (!this._renderer) return;

    // Setup debounced setSource
    this._debouncedSetSource = debounce(
      ({
        source,
        createUndoPoint,
      }: {
        source: VideoSource;
        createUndoPoint: boolean;
      }) => {
        this._hasPendingSetSource = false; // Clear flag when actually running
        // Only proceed if we have a renderer
        if (!this._renderer) return;

        // Now we can send to the renderer
        this._logger.info('Setting source in renderer', {
          sourceElementCount: source.elements?.length,
          createUndoPoint,
        });

        // Record when we're making this change so the debounced source refresh doesn't override it
        this._lastStateChangeTime = Date.now();

        // Track renderer.setSource() call for video loading workflow
        let actionId: string | undefined;
        if (this.currentVideoLoadingWorkflowId) {
          actionId = this.rootStore.analyticsStore.startUserAction(
            ActionEventType.RENDERER_SET_SOURCE,
            ActionType.WORKFLOW,
            this.currentVideoLoadingWorkflowId,
            undefined,
            { sourceElementCount: source.elements?.length, createUndoPoint }
          );
        }

        try {
          // Send to renderer
          this._renderer.setSource(source, createUndoPoint);

          // Track success
          if (actionId) {
            this.rootStore.analyticsStore.completeUserAction(actionId);
          }
        } catch (error) {
          // Track failure
          if (actionId) {
            this.rootStore.analyticsStore.failUserAction(actionId, error as Error);
          }
          throw error;
        }
      },
      this._debounceTimeMs,
      { leading: false, trailing: true },
    );

    // No separate debounced applyModifications – everything now funnels through
    // the setSource debouncer defined above.
  }

  /**
   * Setup the state change handler to intercept and forward renderer state changes
   */
  private _setupStateChangeHandler(): void {
    if (!this._renderer) return;

    // Set our own handler that will forward state changes
    const newHandler = async (state: RendererState) => {
      // Set flag to prevent recursive updates

      try {
        // Record when this state change occurred
        this._lastStateChangeTime = Date.now();

        this.fetchMediaDurations(state);

        // We'll debounce source updates instead of immediately updating on every state change
        // This prevents potentially stale state from overwriting in-progress work
        this._scheduleSourceRefresh();

        // Verify that the renderer source matches our tracked source (if we have a source)
        if (Object.keys(this._source).length > 0) {
          const currentRendererSource = this._renderer!.getSource();
          // Basic comparison of element counts to detect major differences
          const ourElementCount = this._source.elements?.length || 0;
          const rendererElementCount =
            currentRendererSource.elements?.length || 0;

          if (ourElementCount !== rendererElementCount) {
            this._logger.error('Source mismatch detected', {
              ourElementCount,
              rendererElementCount,
              ourSource: this._source,
              rendererSource: currentRendererSource,
            });
          }
        }

        // Notify all registered callbacks
        for (const callback of this._stateChangeCallbacks) {
          await Promise.resolve(callback(state));
        }

        // Execute one-time callbacks and mark them as fired
        if (this._oneTimeStateChangeCallbacks.length > 0) {
          // Create a separate array of callbacks to execute to avoid issues with callbacks
          // that might register new one-time callbacks
          const callbacksToExecute = this._oneTimeStateChangeCallbacks.filter(
            entry => !entry.fired,
          );

          // Mark all as fired before execution to prevent re-execution if there's an error
          for (const entry of callbacksToExecute) {
            entry.fired = true;
          }

          // Execute each callback
          for (const entry of callbacksToExecute) {
            try {
              await Promise.resolve(entry.callback(state));
            } catch (error) {
              console.error('Error in one-time state change callback:', error);
            }
          }

          // Remove all fired callbacks
          this._oneTimeStateChangeCallbacks =
            this._oneTimeStateChangeCallbacks.filter(entry => !entry.fired);
        }

        // Call the original handler if it exists
        if (this._originalOnStateChange) {
          await Promise.resolve(this._originalOnStateChange(state));
        }
      } finally {
        // Clear the flag
      }
    };

    // Explicitly assign the new handler to the renderer
    this._renderer.onStateChange = newHandler;
  }

  /**
   * Schedule a source refresh after the debounce period
   * This prevents frequent source updates during rapid state changes
   */
  private _scheduleSourceRefresh(): void {
    // Cancel any pending refresh
    if (this._pendingSourceRefreshTimeoutId !== null) {
      clearTimeout(this._pendingSourceRefreshTimeoutId);
      this._pendingSourceRefreshTimeoutId = null;
    }

    // Only schedule a refresh if we have a renderer and the debounce is not already in progress
    if (!this.hasPendingSetSource) {
      // Schedule a new refresh - Do we want this?
      this._logger.info('Scheduling source refresh after debounce period');
      this._pendingSourceRefreshTimeoutId = setTimeout(() => {
        // Only update if we have a renderer and no state changes have occurred during the debounce period
        if (this._hasPendingSetSource) {
          this._logger.info(
            'Skipping source refresh due to pending setSource operation',
          );
          return;
        }
        if (
          this._renderer &&
          Date.now() - this._lastStateChangeTime >=
            this._debounceSourceRefreshMs
        ) {
          // Update our local source after the debounce period
          const rendererSource = this._renderer.getSource();
          this._logger.info('Refreshing source after debounce period', {
            timeSinceLastStateChange: Date.now() - this._lastStateChangeTime,
            hasPendingSetSource: this._hasPendingSetSource,
          });
          this.fillMediaDurations(rendererSource);
          this._source = rendererSource;
          this.executeOnetimeSourceRefreshCallbacks();
        }

        this._pendingSourceRefreshTimeoutId = null;
      }, this._debounceSourceRefreshMs);
    }
  }

  private executeOnetimeSourceRefreshCallbacks(): void {
    // Notify all one-time source refresh callbacks
    // Execute one-time callbacks and mark them as fired
    if (this._oneTimeSourceRefreshCallbacks.length > 0) {
      // Create a separate array of callbacks to execute to avoid issues with callbacks
      // that might register new one-time callbacks
      const callbacksToExecute = this._oneTimeSourceRefreshCallbacks.filter(
        entry => !entry.fired,
      );

      // Mark all as fired before execution to prevent re-execution if there's an error
      for (const entry of callbacksToExecute) {
        entry.fired = true;
      }

      // Execute each callback
      for (const entry of callbacksToExecute) {
        try {
          entry.callback(this._source);
        } catch (error) {
          console.error('Error in one-time source refresh callback:', error);
        }
      }

      // Remove all fired callbacks
      this._oneTimeSourceRefreshCallbacks =
        this._oneTimeSourceRefreshCallbacks.filter(entry => !entry.fired);
    }
  }

  private fetchMediaDurations(state: RendererState): void {
    // Fetch media durations
    state.elements?.forEach(item => {
      this._sourceMediaDurationMap.set(
        item.source.source,
        item.mediaDuration
          ? (item.source.trim_start ?? 0) + item.mediaDuration
          : null,
      );
    });
  }

  private fillMediaDurations(source: VideoSource): void {
    source.elements?.forEach(el => {
      el.media_duration = this._sourceMediaDurationMap.get(el.source) ?? null;
    });
  }
}
